import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DateAdapter} from '@angular/material/core';
import {StorageService} from '../storage/storage.service';
import {CentreBasedTranslation, TranslationService} from '../../generated/translation-client';
import {UsersService} from '../../generated/sales-terminal-client';
import {UtilService} from '../util/util.service';
import {environment} from '../../../environments/environment';
import moment from 'moment';
import {Observable, of, Subject, tap} from 'rxjs';
import {TranslationValue} from '../../generated/sales-terminal-client';
import {cloneDeep, findIndex} from 'lodash-es';
import {SharedUtilService} from "../util/shared-util.service";

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  static STORAGE_LANGUAGE_SELECTED = 'language.selected';
  static STORAGE_LANGUAGE_UPDATED = 'language.updated';
  static STORAGE_LANGUAGES = 'language.values';

  private _languages: string[] = [];
  private _selectedLang: string;
  private _locale: string;
  public caller: boolean;

  get languages(): string[] {
    return this._languages;
  }
  set languages(value) {
    this._languages = value;
  }

  get selectedLang(): string {
    return this._selectedLang;
  }
  set selectedLang(value) {
    this._selectedLang = value;
  }

  get locale(): string {
    return this._locale;
  }
  set locale(value) {
    this._locale = value;
    moment.locale(value);
    this.adapter.setLocale(value);
  }

  public consumerListener: Subject<boolean> = new Subject<boolean>();
  public sortedWebShopLanguages: string[] = [];
  public checkFired: boolean;

  constructor(private translateService: TranslateService,
              private adapter: DateAdapter<any>,
              private storageService: StorageService,
              private usersService: UsersService,
              private translationService: TranslationService,
              private sharedUtilService: SharedUtilService,
              private utilService: UtilService) {
    this.restoreLanguages();
    this.checkLang();
  }

  public updateLanguages(): void {
    if (!this.languages?.length) {
      this.getLanguages().subscribe((languages) => {
        this.languages = languages;
        this.storageService.set(LanguageService.STORAGE_LANGUAGES, JSON.stringify(languages));
      });
    }
  }

  public checkLang(): void {
    if (!this.checkFired) {
      this.checkFired = true;
      const storedLang = this.storageService.get(LanguageService.STORAGE_LANGUAGE_SELECTED);
      this.changeLang(storedLang);
    }
  }

  public restoreLanguages(): void {
    const languages = this.storageService.get(LanguageService.STORAGE_LANGUAGES);
    if (languages) {
      this.languages = JSON.parse(languages);
    }
  }

  public getLanguages(): Observable<string[]> {
    if (this.languages.length) {
      return of(this.languages);
    }
    return this.translationService.getTranslationLanguages().pipe(
      tap({error: (e) => console.error(e)})
    );
  }

  public updateDefaultLanguage(lang: string): Observable<any> {
    return this.usersService.usersLanguagePut({ defaultLanguage: lang }).pipe(
      tap({
        error: (e) => console.error(e),
        next: () => this.storageService.set(LanguageService.STORAGE_LANGUAGE_UPDATED, true)
      })
    );
  }

  public getCentreTranslations(): Observable<CentreBasedTranslation> {
    return this.translationService.getCentreBasedTranslations(this.selectedLang);
  }

  public changeLang(language: string = '', caller = false, saveToBackEnd = false): void {
    if (language === this.selectedLang) {
      return;
    }
    this.caller = caller;
    const userLang = (navigator as any).language || (navigator as any).userLanguage;
    if (!language) {
      language = this.selectedLang || (userLang && userLang.split('-')[0].toLowerCase()) || environment.defaultLanguage;
    }
    switch (language) {
      case 'et':
        this.locale = 'et';
        break;
      case 'en':
        this.locale = 'en-gb';
        break;
      case 'ru':
        this.locale = 'ru';
        break;
      case 'lv':
        this.locale = 'lv';
        break;
      case 'lt':
        this.locale = 'lt';
        break;
      case 'fi':
        this.locale = 'fi';
        break;
     default:
        this.locale = 'en-gb';
        break;
    }
    this.utilService.translationsLoadedChange.emit(false);
    this.sharedUtilService.translationNamespacesLoaded = {};
    this.translateService.resetLang(this.selectedLang);
    this.selectedLang = language;
    if (this.storageService.get(LanguageService.STORAGE_LANGUAGE_SELECTED) !== this.selectedLang) {
      this.storageService.set(LanguageService.STORAGE_LANGUAGE_SELECTED, this.selectedLang);
    }
    this.translateService.use(this.selectedLang).subscribe(() => this.utilService.translationsLoadedChange.emit(true));
    if (saveToBackEnd) {
      this.updateDefaultLanguage(this.selectedLang).subscribe();
    }
    setTimeout(() => this.checkFired = false, 1000);
  }

  public sortLanguages(languageList: string[], defaultLang?: string): string[] {
    /* Add English as first */
    if (languageList.includes('en')) {
      languageList.splice(languageList.indexOf('en'), 1);
      languageList.splice(0, 0, 'en');
    }
    /* Add Salespoint default language as second */
    if (defaultLang && languageList.includes(defaultLang)) {
      languageList.splice(languageList.indexOf(defaultLang), 1);
      languageList.splice(1, 0, defaultLang);
    }
    /* Add Russian as third unless it is the default language */
    if (defaultLang !== 'ru' && languageList.includes('ru')) {
      languageList.splice(languageList.indexOf('ru'), 1);
      languageList.splice(2, 0, 'ru');
    }
    return languageList;
  }

  public sortWebShopLanguages(languageList: string[], defaultLang: string, skipUpdate?: boolean): string[] {
    if (languageList.includes(defaultLang)) {
      this.moveLanguageToIndex(defaultLang, languageList, 0);
    }
    if (defaultLang !== 'en' && languageList.includes('en')) {
      this.moveLanguageToIndex('en', languageList, 1);
    } else if (defaultLang !== 'et' && languageList.includes('et')) {
      this.moveLanguageToIndex('et', languageList, 1);
    }
    if (defaultLang !== 'ru' && languageList.includes('ru')) {
      this.moveLanguageToIndex('ru', languageList, 2);
    }
    if (defaultLang !== 'fi' && languageList.includes('fi')) {
      this.moveLanguageToIndex('fi', languageList, 3);
    }
    if (skipUpdate) {
      return languageList;
    }
    this.sortedWebShopLanguages = languageList.slice(0, 4);
    return this.sortedWebShopLanguages;
  }

  public moveLanguageToIndex(lang: string, languageList: string[], index: number) {
    languageList.splice(languageList.indexOf(lang), 1);
    languageList.splice(index, 0, lang);
  }

  public sortWebShopLanguagesForTranslations(translationList: TranslationValue[], defaultLang: string): TranslationValue[] {
    if (!translationList?.length) {
      return [];
    }
    this.moveTranslationToIndex(defaultLang, translationList, 0);

    if (defaultLang !== 'en') {
      this.moveTranslationToIndex('en', translationList, 1);
    }
    if (defaultLang !== 'ru') {
      this.moveTranslationToIndex('ru', translationList, 2);
    }
    if (defaultLang !== 'fi') {
      this.moveTranslationToIndex('fi', translationList, 3);
    }
    return translationList.slice(0, 4);
  }

  private moveTranslationToIndex(lang: string, translationList: TranslationValue[], index: number) {
    const langIndex = findIndex(translationList, translation => translation.language === lang);
    if (langIndex >= 0) {
      const translation = cloneDeep(translationList[langIndex]);
      translationList.splice(langIndex, 1);
      translationList.splice(index, 0, translation);
    }
  }
}
